import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Container, Button } from 'react-bootstrap';
import { useThemeMode } from '../../ThemeContext';
import LightDashboard from '../../assets/landing_page_images/Light-Dashboard.webp';
import DarkDashboard from '../../assets/landing_page_images/Dark-Dashboard.webp';
import LightDocument from '../../assets/landing_page_images/Light-Document.webp';
import DarkDocument from '../../assets/landing_page_images/Dark-Document.webp';
import LightDocumentUpload from '../../assets/landing_page_images/Light-DocumentUploadFlow.webp';
import DarkDocumentUpload from '../../assets/landing_page_images/Dark-DocumentUploadFlow.webp';
import LightManageUser from '../../assets/landing_page_images/Light-ManageUser.webp';
import DarkManageUser from '../../assets/landing_page_images/Dark-ManageUser.webp';
import Mobile1 from '../../assets/landing_page_images/mobile1.webp';
import Mobile2 from '../../assets/landing_page_images/mobile2.webp';
import SeoPageLayout from './SeoPageLayout';
import useSeoMeta from './useSeoMeta';
import { getPublicSeoPage } from '../../seo/publicPages';
import './IncidentManagementSoftware.css';

const HELP_FEATURES = [
  {
    title: 'Record incidents and hazards digitally',
    icon: 'form',
    paragraphs: [
      'Use digital WHS forms to capture incident and hazard information without relying on paper-based records. Forms can include dynamic fields and signature capture, and can be accessed through mobile devices and QR codes.',
    ],
  },
  {
    title: 'Capture evidence',
    icon: 'photo',
    paragraphs: [
      "Incident and hazard reports can include relevant information and supporting evidence. Complimate's mobile functionality also supports photo and evidence capture from the field.",
    ],
  },
  {
    title: 'Turn findings into actions',
    icon: 'task',
    paragraphs: [
      'An incident or hazard should not simply remain in a completed form. Complimate can connect findings with tasks and corrective actions, allowing an assigned person, due date, attachments, comments and other action details to be managed through the workflow.',
    ],
  },
  {
    title: 'Keep records connected',
    icon: 'link',
    paragraphs: [
      'Digital form submissions can be linked with sites, projects, employees and other records. This gives teams a more structured evidence trail instead of keeping incident information across disconnected files or spreadsheets.',
    ],
  },
];

const FEATURE_TABLE = [
  { feature: 'Incident reporting', helps: 'Record workplace incidents digitally', icon: 'alert' },
  { feature: 'Hazard reporting', helps: 'Capture hazards and safety concerns', icon: 'hazard' },
  { feature: 'Digital WHS forms', helps: 'Replace paper-based reporting with digital records', icon: 'form' },
  { feature: 'QR code access', helps: 'Give workers mobile access to relevant forms', icon: 'qr' },
  { feature: 'Photo and evidence capture', helps: 'Attach supporting information from the field', icon: 'photo' },
  { feature: 'Corrective actions', helps: 'Turn findings into trackable follow-up actions', icon: 'task' },
  { feature: 'Task management', helps: 'Assign owners, priorities, deadlines and evidence', icon: 'clipboard' },
  { feature: 'Site records', helps: 'Connect forms and documents with specific locations', icon: 'pin' },
  { feature: 'Submission history', helps: 'Maintain access to completed form records', icon: 'history' },
  { feature: 'PDF export', helps: 'Export completed forms for record keeping and audits', icon: 'pdf' },
];

const WORKFLOW_STEPS = [
  {
    title: '1. Report the incident or hazard',
    icon: 'form',
    text: 'A worker or team member completes the relevant digital form.',
  },
  {
    title: '2. Capture supporting information',
    icon: 'photo',
    text: 'Add the required details, signatures and available evidence, including photos where relevant.',
  },
  {
    title: '3. Store the submission',
    icon: 'history',
    text: 'The completed record remains available in the digital form submission history and can be associated with the relevant site, project or employee.',
  },
  {
    title: '4. Create follow-up actions',
    icon: 'task',
    text: 'Where an issue requires further work, create a task or corrective action and assign responsibility.',
  },
  {
    title: '5. Track progress',
    icon: 'dashboard',
    text: 'Monitor status, deadlines, attachments, comments and completion.',
  },
  {
    title: '6. Keep the evidence trail',
    icon: 'folder',
    text: 'Completed forms and related records can be retained as part of the broader compliance workflow.',
  },
];

const WORKFLOW_CHAIN = [
  'incident reporting',
  'evidence',
  'corrective action',
  'task completion',
];

const AUDIENCE = [
  { text: 'WHS and HSE managers', icon: 'shield' },
  { text: 'Compliance officers', icon: 'clipboard' },
  { text: 'Quality managers', icon: 'badge' },
  { text: 'Site supervisors', icon: 'hardhat' },
  { text: 'Operations managers', icon: 'dashboard' },
  { text: 'Businesses managing multiple sites', icon: 'pin' },
  { text: 'Consultants supporting client compliance', icon: 'users' },
];

const BENEFITS = [
  { text: 'Reduce reliance on paper incident reports', icon: 'form' },
  { text: 'Capture information directly from the field', icon: 'mobile' },
  { text: 'Keep incident and hazard records organised', icon: 'folder' },
  { text: 'Connect evidence with follow-up actions', icon: 'link' },
  { text: 'Assign responsibility for corrective work', icon: 'users' },
  { text: 'Track outstanding compliance tasks', icon: 'task' },
  { text: 'Keep site-related records together', icon: 'pin' },
  { text: 'Build a clearer record for internal reviews and audits', icon: 'pdf' },
];

const FAQ_ITEMS = [
  {
    question: 'What is incident management software?',
    answer:
      'Incident management software is a digital system for recording workplace incidents and related safety issues, maintaining evidence and managing follow-up actions. Complimate provides incident and hazard reporting as part of its wider WHS and compliance platform.',
  },
  {
    question: 'Can Complimate be used for hazard reporting?',
    answer:
      'Yes. Hazards are one of the live functionality areas supported by Complimate. Digital forms can be used for mobile hazard and incident reporting.',
  },
  {
    question: 'Can workers report incidents from a mobile device?',
    answer:
      'Yes. Complimate supports mobile access to core compliance features, including digital forms, photo evidence and incident or hazard reporting workflows.',
  },
  {
    question: 'Can an incident lead to a corrective action?',
    answer:
      "Yes. Complimate's project and task functionality supports turning findings into assigned, trackable corrective actions with owners, due dates, evidence and completion status.",
  },
  {
    question: 'Can incident forms be accessed using a QR code?',
    answer:
      "Yes. Complimate's Digital Forms system supports QR codes that can be printed or downloaded and accessed on mobile devices. The documented use cases include mobile incident and hazard reporting.",
  },
  {
    question: 'Does Complimate provide legal advice about workplace incidents?',
    answer:
      'No. Complimate should not be presented as providing legal advice. Its AI-assisted functionality and compliance workflows are intended to help businesses manage and document their WHS and compliance processes.',
  },
];

const { path: PAGE_PATH, title: PAGE_TITLE, description: PAGE_DESCRIPTION } =
  getPublicSeoPage('/incident-management-software/');

const ICON_PATHS = {
  alert: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v5" />
      <path d="M12 16h.01" />
    </>
  ),
  hazard: (
    <>
      <path d="M12 3 22 20H2L12 3Z" />
      <path d="M12 9v5" />
      <path d="M12 17h.01" />
    </>
  ),
  form: (
    <>
      <rect x="5" y="3" width="14" height="18" rx="2" />
      <path d="M8 8h8M8 12h8M8 16h5" />
    </>
  ),
  photo: (
    <>
      <rect x="3" y="5" width="18" height="15" rx="2" />
      <circle cx="9" cy="10" r="2" />
      <path d="m21 16-5-5L5 20" />
    </>
  ),
  task: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="3" />
      <path d="m8 12 2.4 2.4L16 9" />
    </>
  ),
  link: (
    <>
      <path d="M10 13a5 5 0 0 0 7.07 0l1.41-1.41a5 5 0 0 0-7.07-7.07L10 5.93" />
      <path d="M14 11a5 5 0 0 0-7.07 0L5.52 12.41a5 5 0 0 0 7.07 7.07L14 18.07" />
    </>
  ),
  qr: (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <path d="M14 14h4v4h-4zM20 14v3M14 20h3" />
    </>
  ),
  clipboard: (
    <>
      <path d="M8 4h8" />
      <path d="M9 3v3h6V3" />
      <path d="M6 5H4v16h16V5h-2" />
      <path d="m8 13 2.2 2.2L16 9.5" />
    </>
  ),
  pin: (
    <>
      <path d="M12 21s7-6.2 7-11a7 7 0 1 0-14 0c0 4.8 7 11 7 11Z" />
      <circle cx="12" cy="10" r="2.5" />
    </>
  ),
  history: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  pdf: (
    <>
      <path d="M6 3h8l4 4v14H6V3Z" />
      <path d="M14 3v5h5" />
      <path d="M9 13h6M9 17h4" />
    </>
  ),
  folder: (
    <>
      <path d="M3 6h7l2 2h9v11H3V6Z" />
      <path d="M3 10h18" />
    </>
  ),
  dashboard: (
    <>
      <rect x="3" y="3" width="8" height="8" rx="1.5" />
      <rect x="13" y="3" width="8" height="5" rx="1.5" />
      <rect x="13" y="10" width="8" height="11" rx="1.5" />
      <rect x="3" y="13" width="8" height="8" rx="1.5" />
    </>
  ),
  shield: <path d="M12 3 5 6v5c0 4.6 2.9 7.6 7 10 4.1-2.4 7-5.4 7-10V6l-7-3Z" />,
  users: (
    <>
      <circle cx="9" cy="9" r="3" />
      <circle cx="17" cy="10" r="2.5" />
      <path d="M3 20c.5-4 2.5-6 6-6s5.5 2 6 6" />
      <path d="M15 15c3 0 5 1.7 5.5 5" />
    </>
  ),
  badge: (
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="m9 12 2 2 4-4" />
    </>
  ),
  hardhat: (
    <>
      <path d="M4 14a8 8 0 0 1 16 0" />
      <path d="M3 14h18v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-3Z" />
      <path d="M12 4v3" />
      <path d="M9 19h6" />
    </>
  ),
  mobile: (
    <>
      <rect x="7" y="2" width="10" height="20" rx="2" />
      <path d="M11 18h2" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v6" />
      <path d="M12 8h.01" />
    </>
  ),
};

function FeatureIcon({ name }) {
  return (
    <span className="ims-feature-icon" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        {ICON_PATHS[name] || ICON_PATHS.alert}
      </svg>
    </span>
  );
}

function IncidentManagementSoftware() {
  const [openFaq, setOpenFaq] = useState(0);
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';

  useSeoMeta({
    title: PAGE_TITLE,
    description: PAGE_DESCRIPTION,
    path: PAGE_PATH,
  });

  return (
    <SeoPageLayout>
      <div className="ims-page">
        <section className="ims-hero">
          <Container>
            <div className="ims-hero-grid">
              <div className="ims-hero-copy">
                <p className="seo-eyebrow">
                  <FeatureIcon name="alert" />
                  Incident Management Software
                </p>
                <h1 className="seo-h1">
                  Incident Management Software for Australian Businesses
                </h1>
                <p className="seo-lead">
                  Complimate is incident management software that helps Australian
                  businesses record incidents and hazards, capture evidence, assign
                  follow-up actions and keep compliance records organised in one
                  platform.
                </p>
                <p className="seo-body">
                  Instead of treating an incident report as a standalone form,
                  Complimate connects incident and hazard reporting with digital
                  forms, tasks, corrective actions, documents and compliance
                  workflows. This helps teams create a clearer record of what
                  happened, what needs to be done and who is responsible.
                </p>
                <div className="seo-cta-row">
                  <Button
                    as={Link}
                    to="/contact-us"
                    className="btn-teal ims-primary-button"
                    id="ims_book_demo_hero"
                  >
                    Book a Demo
                    <span aria-hidden="true">→</span>
                  </Button>
                </div>
              </div>

              <div className="ims-hero-visual">
                <div className="ims-visual-glow" aria-hidden="true" />
                <div className="ims-product-window">
                  <div className="ims-window-bar" aria-hidden="true">
                    <span />
                    <span />
                    <span />
                  </div>
                  <img
                    src={isDark ? DarkDashboard : LightDashboard}
                    alt="Complimate dashboard for incident reporting, hazard records and compliance tasks"
                    width="960"
                    height="680"
                    fetchPriority="high"
                  />
                </div>
                <div className="ims-float-card ims-float-card-top" aria-hidden="true">
                  <FeatureIcon name="alert" />
                  <span>Incident reporting</span>
                </div>
                <div className="ims-float-card ims-float-card-bottom" aria-hidden="true">
                  <FeatureIcon name="hazard" />
                  <span>Hazard reporting</span>
                </div>
              </div>
            </div>
          </Container>
        </section>

        <section className="seo-section ims-intro-section">
          <Container>
            <div className="ims-intro-grid">
              <div>
                <div className="ims-section-marker">
                  <FeatureIcon name="shield" />
                </div>
                <h2 className="seo-h2">What Is Incident Management Software?</h2>
                <p className="seo-body">
                  Incident management software is a digital system used to record,
                  manage and follow up workplace incidents and related safety
                  issues.
                </p>
                <p className="seo-body">
                  Complimate provides incident and hazard reporting as part of a
                  broader WHS and compliance management platform. Workers and site
                  teams can use digital forms to report incidents or hazards, while
                  managers can connect the resulting information to tasks, evidence
                  and corrective actions.
                </p>
                <p className="seo-body">
                  This approach is useful for businesses that want more than a basic
                  incident reporting form and need to connect incidents with their
                  wider work health and safety processes.
                </p>
              </div>
              <div className="ims-shot-frame">
                <img
                  src={isDark ? DarkDocument : LightDocument}
                  alt="Complimate digital records for incident reports, hazard forms and WHS documents"
                  width="960"
                  height="680"
                />
              </div>
            </div>
          </Container>
        </section>

        <section className="seo-section ims-help-section">
          <Container>
            <div className="ims-section-intro">
              <h2 className="seo-h2">How Does Complimate Help Manage Incidents?</h2>
              <p className="seo-body">
                Complimate brings incident reporting into the same workflow used
                for other compliance activities.
              </p>
            </div>
            <div className="ims-help-grid">
              {HELP_FEATURES.map((item, index) => (
                <article className="ims-help-card" key={item.title}>
                  <div className="ims-card-topline">
                    <FeatureIcon name={item.icon} />
                    <span>0{index + 1}</span>
                  </div>
                  <h3 className="seo-h3">{item.title}</h3>
                  {item.paragraphs.map((paragraph) => (
                    <p key={paragraph.slice(0, 48)} className="seo-body">
                      {paragraph}
                    </p>
                  ))}
                </article>
              ))}
            </div>
          </Container>
        </section>

        <section className="seo-section ims-features-section">
          <Container>
            <div className="ims-features-intro">
              <div>
                <div className="ims-section-marker">
                  <FeatureIcon name="clipboard" />
                </div>
                <h2 className="seo-h2">
                  What Incident Management Features Does Complimate Include?
                </h2>
              </div>
              <div className="ims-shot-frame ims-shot-frame-soft">
                <img
                  src={isDark ? DarkDocumentUpload : LightDocumentUpload}
                  alt="Complimate digital WHS forms used for incident and hazard reporting"
                  width="960"
                  height="680"
                />
              </div>
            </div>
            <div className="seo-table-wrap ims-spec-table">
              <table className="seo-table">
                <thead>
                  <tr>
                    <th scope="col">Feature</th>
                    <th scope="col">How it helps</th>
                  </tr>
                </thead>
                <tbody>
                  {FEATURE_TABLE.map((row) => (
                    <tr key={row.feature}>
                      <th scope="row">
                        <span className="ims-table-feature">
                          <FeatureIcon name={row.icon} />
                          {row.feature}
                        </span>
                      </th>
                      <td>{row.helps}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="seo-body seo-diff-note">
              These capabilities are based on Complimate&apos;s documented live
              product functionality.
            </p>
          </Container>
        </section>

        <section className="seo-section ims-workflow-section">
          <Container>
            <div className="ims-section-intro">
              <h2 className="seo-h2">
                How Does Incident Reporting Work in Complimate?
              </h2>
              <p className="seo-body">
                A typical workflow can be structured as follows:
              </p>
            </div>
            <div className="ims-steps">
              {WORKFLOW_STEPS.map((step, index) => (
                <article key={step.title} className="ims-step">
                  <div className="ims-step-number">{index + 1}</div>
                  <FeatureIcon name={step.icon} />
                  <div>
                    <h3 className="seo-h4">{step.title}</h3>
                    <p className="seo-body mb-0">{step.text}</p>
                  </div>
                </article>
              ))}
            </div>
            <div className="ims-chain-card">
              <div className="ims-chain-copy">
                <FeatureIcon name="link" />
                <p className="seo-body mb-0">
                  This connects{' '}
                  <strong>
                    incident reporting → evidence → corrective action → task
                    completion
                  </strong>
                  , rather than leaving the incident as an isolated record.
                  Complimate&apos;s product documentation specifically describes this
                  connected workflow as part of its overall platform approach.
                </p>
              </div>
              <ol className="ims-chain" aria-hidden="true">
                {WORKFLOW_CHAIN.map((item, index) => (
                  <li key={item}>
                    <span>{item}</span>
                    {index < WORKFLOW_CHAIN.length - 1 && (
                      <span className="ims-chain-arrow">→</span>
                    )}
                  </li>
                ))}
              </ol>
            </div>
            <div className="ims-mobile-note">
              <div className="ims-mobile-copy">
                <FeatureIcon name="mobile" />
                <p className="seo-body mb-0">
                  Complimate&apos;s mobile functionality also supports photo and
                  evidence capture from the field.
                </p>
              </div>
              <div className="ims-mobile-shots">
                <img
                  src={Mobile1}
                  alt="Complimate mobile app used to report a workplace incident from the field"
                  width="220"
                  height="440"
                />
                <img
                  src={Mobile2}
                  alt="Complimate mobile app showing field evidence capture for an incident report"
                  width="220"
                  height="440"
                />
              </div>
            </div>
          </Container>
        </section>

        <section className="seo-section ims-cta-section">
          <Container>
            <div className="ims-cta-card">
              <div className="ims-cta-orb ims-cta-orb-one" aria-hidden="true" />
              <div className="ims-cta-orb ims-cta-orb-two" aria-hidden="true" />
              <div className="ims-cta-copy">
                <h2 className="seo-h2">Manage Incidents From Report to Follow-Up</h2>
                <p className="seo-body">
                  Keep incident reporting connected to the rest of your WHS
                  management system. With Complimate, teams can capture incidents
                  and hazards digitally, collect evidence and manage follow-up
                  actions in one compliance platform.
                </p>
              </div>
              <Button
                as={Link}
                to="/contact-us"
                className="btn-teal ims-cta-button"
                id="ims_book_demo_mid"
              >
                Book a Demo
                <span aria-hidden="true">→</span>
              </Button>
            </div>
          </Container>
        </section>

        <section className="seo-section ims-audience-section">
          <Container>
            <div className="ims-audience-intro">
              <div>
                <div className="ims-section-marker">
                  <FeatureIcon name="users" />
                </div>
                <h2 className="seo-h2">
                  Who Is Complimate Incident Management Software For?
                </h2>
                <p className="seo-body">
                  Complimate is designed for Australian businesses that need to
                  manage incidents as part of a broader WHS and compliance system.
                </p>
                <p className="seo-body">It can be relevant to:</p>
              </div>
              <div className="ims-shot-frame">
                <img
                  src={isDark ? DarkManageUser : LightManageUser}
                  alt="Complimate records for WHS managers, site supervisors and compliance teams"
                  width="960"
                  height="680"
                />
              </div>
            </div>
            <ul className="ims-look-grid">
              {AUDIENCE.map((item) => (
                <li className="ims-look-card" key={item.text}>
                  <FeatureIcon name={item.icon} />
                  <span>{item.text}</span>
                </li>
              ))}
            </ul>
            <p className="seo-body">
              The platform&apos;s target industries include construction,
              childcare, health, not-for-profit and local government.
            </p>
            <div className="ims-note">
              <FeatureIcon name="info" />
              <p className="seo-body">
                For example, a construction supervisor can use a mobile form to
                report a site incident or hazard, capture evidence and then
                connect the finding to a corrective action for follow-up.
              </p>
            </div>
          </Container>
        </section>

        <section className="seo-section ims-benefits-section">
          <Container>
            <div className="ims-section-intro">
              <h2 className="seo-h2">
                What Are the Benefits of Using Incident Management Software?
              </h2>
              <p className="seo-body">
                A connected incident management process can help teams:
              </p>
            </div>
            <ul className="ims-benefits-grid">
              {BENEFITS.map((item) => (
                <li className="ims-benefit-card" key={item.text}>
                  <FeatureIcon name={item.icon} />
                  <span>{item.text}</span>
                </li>
              ))}
            </ul>
            <p className="seo-body ims-look-note">
              Complimate is designed around this broader approach to WHS
              management rather than treating incidents, inspections, documents
              and corrective actions as completely separate processes.
            </p>
          </Container>
        </section>

        <section className="seo-section ims-faq-section" id="faq">
          <Container>
            <div className="seo-section-header text-center">
              <h2 className="seo-h2 seo-faq-heading">Frequently Asked Questions</h2>
            </div>
            <div className="seo-faq-list">
              {FAQ_ITEMS.map((item, index) => {
                const isOpen = openFaq === index;
                return (
                  <div
                    key={item.question}
                    className={`seo-faq-item ${isOpen ? 'open' : ''}`}
                  >
                    <button
                      type="button"
                      className="seo-faq-question"
                      aria-expanded={isOpen}
                      onClick={() => setOpenFaq(isOpen ? -1 : index)}
                    >
                      <h3 className="seo-h5">{item.question}</h3>
                      <span className="seo-faq-icon" aria-hidden="true">
                        {isOpen ? '−' : '+'}
                      </span>
                    </button>
                    {isOpen && (
                      <p className="seo-faq-answer seo-body">{item.answer}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </Container>
        </section>
      </div>
    </SeoPageLayout>
  );
}

export default IncidentManagementSoftware;
